import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { StationForm } from "../../components/StationForm";
import { getStation, getStationQrCode, updateStation } from "../../api/station";
import type { StationFormData } from "../../schemas/station";
import { Loader } from "lucide-react";
import { toast } from "react-toastify";

export function EditStation() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: station,
    isLoading: isLoadingStation,
    error,
  } = useQuery({
    queryKey: ["station", id],
    queryFn: () => getStation(id!),
    enabled: !!id,
  });

  const { data: qrCode } = useQuery({
    queryKey: ["station-qr-code", id],
    queryFn: () => getStationQrCode(id!),
    enabled: !!id,
  });

  const mutation = useMutation({
    mutationFn: (data: StationFormData) => updateStation(id!, data),
    onSuccess: () => {
      setTimeout(() => {
        navigate("/admin/dashboard");
      }, 1000);
    },
    onError: (error) => {
      if (
        error.message ===
        "A station with this name and connector ID already exists"
      ) {
        toast.error("A station with this name and connector ID already exists");
      }
    },
  });

  const handleSubmit = async (data: StationFormData) => {
    await mutation.mutateAsync(data);
  };

  if (isLoadingStation) {
    return (
      <div className="min-h-screen bg-green-600 flex items-center justify-center">
        <Loader className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }
  if (error?.message === "Station not found") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <h1 className="text-2xl font-bold">Station not found</h1>
      </div>
    );
  }
  return (
    <div className="min-h-screen w-full bg-gray-50">
      <div className="min-h-screen w-full flex items-start pt-8 justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Edit Station
          </h2>
          <StationForm
            initialData={station}
            onSubmit={handleSubmit}
            isLoading={mutation.isPending}
            isSuccess={mutation.isSuccess}
          />
          <img src={qrCode?.qrCode} alt="QR Code" />
        </div>
      </div>
    </div>
  );
}
